import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border px-2 py-0.5 font-medium text-xs transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-destructive text-white focus-visible:ring-destructive/20 dark:border-red-400/30 dark:bg-red-500/30 dark:text-red-100 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90 dark:[a&]:hover:bg-red-500/40',
        outline:
          'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        gray: 'border-transparent bg-gray-200 dark:border-gray-400/30 dark:bg-gray-500/20 dark:text-gray-100 [a&]:hover:bg-gray-300 dark:[a&]:hover:bg-gray-500/30',
        green:
          'border-transparent bg-green-200 dark:border-green-400/30 dark:bg-green-500/20 dark:text-green-100 [a&]:hover:bg-green-300 dark:[a&]:hover:bg-green-500/30',
        blue: 'border-transparent bg-blue-200 text-blue-900 dark:border-blue-400/30 dark:bg-blue-500/20 dark:text-blue-100 [a&]:hover:bg-blue-300 dark:[a&]:hover:bg-blue-500/30',
        orange:
          'border-transparent bg-orange-200 text-orange-900 dark:border-orange-400/30 dark:bg-orange-500/20 dark:text-orange-100 [a&]:hover:bg-orange-300 dark:[a&]:hover:bg-orange-500/30',
        red: 'border-transparent bg-red-200 text-red-900 dark:border-red-400/30 dark:bg-red-500/20 dark:text-red-100 [a&]:hover:bg-red-300 dark:[a&]:hover:bg-red-500/30',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp
      className={cn(badgeVariants({ variant }), className)}
      data-slot='badge'
      {...props}
    />
  );
}

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

export type { BadgeVariant };
export { Badge, badgeVariants };
